"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { AnimatePresence, motion } from "framer-motion";
import { Building2, CheckCircle2, Lock, MapPin, Shield } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { StreamingInsights } from "./streaming-insights";

// Define a type for streaming updates
export interface StreamUpdate {
  phase: string;
  data: unknown;
  progress?: number;
  error?: string;
}

interface LoadingModalProps {
  isOpen: boolean;
  city: string;
  state: string;
  address: string | null;
  propertySlug?: string | null;
  analysisId?: string | null;
  isComplete?: boolean;
  requiresSignIn?: boolean;
  isLightAnalysis?: boolean;
  streamUpdates?: StreamUpdate[];
  currentPhase?: string;
  realProgress?: number;
  onSignInClick?: () => void;
  onClose?: () => void;
}

// Map of phases to user-friendly messages
const phaseMessages: Record<string, string> = {
  initialize: "Initializing analysis...",
  property_data: "Analyzing property details...",
  scraping: "Retrieving property information...",
  property_details: "Processing property details...",
  image_processing: "Processing property images...",
  image_analysis: "Analyzing property images...",
  slug: "Creating property identifier...",
  data_fetching: "Gathering market data...",
  ratespot_data: "Analyzing valuation data...",
  comps: "Finding comparable properties...",
  crime_data: "Evaluating neighborhood safety...",
  market_data: "Analyzing local market trends...",
  fun_facts: "Discovering location insights...",
  annotated_images: "Annotating property images...",
  property_image: "Processing property photos...",
  analysis: "Running detailed analysis...",
  market_analysis: "Evaluating market conditions...",
  location_analysis: "Analyzing neighborhood details...",
  value_analysis: "Calculating property value...",
  negotiation_data: "Generating negotiation insights...",
  tour_questions: "Creating tour questions...",
  scores: "Calculating final scores...",
  saving: "Saving analysis results...",
  podcast: "Preparing audio analysis...",
  existing_analysis: "Found existing analysis for this property...",
  complete: "Analysis complete!",
  error: "Error during analysis",
};

export function LoadingModal({
  isOpen,
  city,
  state,
  address,
  propertySlug: initialPropertySlug,
  analysisId: initialAnalysisId,
  isComplete: initialIsComplete = false,
  requiresSignIn = false,
  isLightAnalysis = false,
  streamUpdates = [],
  currentPhase,
  realProgress,
  onSignInClick,
  onClose,
}: LoadingModalProps) {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(
    "Initializing analysis...",
  );
  const [localIsComplete, setLocalIsComplete] = useState(initialIsComplete);
  const [propertySlug, setPropertySlug] = useState(initialPropertySlug);
  const [analysisId, setAnalysisId] = useState(initialAnalysisId);
  const [propertyInfo, setPropertyInfo] = useState<{
    daysOnMarket?: number;
    price?: number;
    beds?: number;
    baths?: number;
    sqft?: number;
    pricePerSqft?: number;
    estimatedValue?: number;
    yearBuilt?: number;
    propertyType?: string;
  }>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimeRef = useRef<number>(Date.now());
  const targetProgressRef = useRef<number>(0);

  // Ensure streamUpdates is always an array before passing to children
  const safeStreamUpdates = Array.isArray(streamUpdates) ? streamUpdates : [];

  // Set up continuous progress bar animation
  useEffect(() => {
    if (!isOpen || localIsComplete || requiresSignIn) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      return;
    }

    // Initialize target progress
    targetProgressRef.current = Math.max(targetProgressRef.current, progress);
    lastUpdateTimeRef.current = Date.now();

    // Set up interval to increment progress
    progressIntervalRef.current = setInterval(() => {
      const currentTime = Date.now();
      const timeSinceLastUpdate = currentTime - lastUpdateTimeRef.current;

      setProgress((currentProgress) => {
        // If we're close to target, slow down
        if (currentProgress >= targetProgressRef.current) {
          // If we haven't received updates in a while, slowly increment
          if (timeSinceLastUpdate > 5000) {
            // Slow creep forward if no updates for 5+ seconds
            // The longer we wait, the slower it gets
            const slowIncrement = Math.max(
              0.05,
              0.5 - timeSinceLastUpdate / 20000,
            );
            return Math.min(99, currentProgress + slowIncrement);
          }
          return currentProgress;
        }

        // Normal catch-up to target (faster)
        const distanceToTarget = targetProgressRef.current - currentProgress;
        const increment = Math.max(0.2, distanceToTarget * 0.05);
        return Math.min(targetProgressRef.current, currentProgress + increment);
      });
    }, 100);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isOpen, localIsComplete, requiresSignIn, progress]);

  // Update progress and message based on streaming updates
  useEffect(() => {
    if (safeStreamUpdates.length > 0) {
      // Get the latest update
      const latestUpdate = safeStreamUpdates[safeStreamUpdates.length - 1];

      // Set message based on the phase
      if (latestUpdate.phase) {
        setCurrentMessage(
          phaseMessages[latestUpdate.phase] ||
            `Processing ${latestUpdate.phase.replace(/_/g, " ")}...`,
        );

        // Handle completion phases
        if (
          latestUpdate.phase === "existing_analysis" ||
          latestUpdate.phase === "complete"
        ) {
          targetProgressRef.current = 100;
          lastUpdateTimeRef.current = Date.now();

          if (latestUpdate.data && typeof latestUpdate.data === "object") {
            const data = latestUpdate.data as {
              slug?: string;
              property_id?: string;
            };

            if (data.slug) {
              setPropertySlug(data.slug);
            }

            if (data.property_id && isLightAnalysis) {
              setAnalysisId(data.property_id);
            }
          }
          setLocalIsComplete(true);
        }

        // Handle property data updates
        if (
          (latestUpdate.phase === "property_data" ||
            latestUpdate.phase === "property_details") &&
          latestUpdate.data &&
          typeof latestUpdate.data === "object"
        ) {
          const data = latestUpdate.data as {
            price?: number;
            beds?: number;
            baths?: number;
            sqft?: number;
            yearBuilt?: number;
            propertyType?: string;
          };

          setPropertyInfo((prev) => ({
            ...prev,
            ...data,
            pricePerSqft:
              data.price && data.sqft
                ? Math.round(data.price / data.sqft)
                : prev.pricePerSqft,
          }));

          // Update progress for property data phase (early progress)
          if (targetProgressRef.current < 30) {
            targetProgressRef.current = 30;
            lastUpdateTimeRef.current = Date.now();
          }
        }

        // Handle market data updates
        if (
          latestUpdate.phase === "market_data" &&
          latestUpdate.data &&
          typeof latestUpdate.data === "object"
        ) {
          const data = latestUpdate.data as {
            marketData?: {
              trends?: {
                averageDaysOnMarket?: number;
              };
            };
            _insights?: {
              marketTrend?: string;
              priceComparison?: string;
            };
            estimatedValue?: number;
          };

          setPropertyInfo((prev) => ({
            ...prev,
            daysOnMarket: data.marketData?.trends?.averageDaysOnMarket,
            estimatedValue: data.estimatedValue,
          }));

          // Mid-point progress update
          if (targetProgressRef.current < 60) {
            targetProgressRef.current = 60;
            lastUpdateTimeRef.current = Date.now();
          }
        }

        // Handle score updates - valuable information near the end
        if (
          latestUpdate.phase === "scores" &&
          latestUpdate.data &&
          typeof latestUpdate.data === "object"
        ) {
          // Update progress for scores (near completion)
          if (targetProgressRef.current < 85) {
            targetProgressRef.current = 85;
            lastUpdateTimeRef.current = Date.now();
          }
        }

        // Handle negotiation data - valuable information near the end
        if (
          latestUpdate.phase === "negotiation_data" &&
          latestUpdate.data &&
          typeof latestUpdate.data === "object"
        ) {
          // For light analysis, check for quickTake
          const data = latestUpdate.data as {
            quickTake?: string;
            keyHighlights?: string[];
            potentialIssues?: string[];
          };

          if (isLightAnalysis && (data.quickTake || data.keyHighlights)) {
            // Update progress for light analysis insights (near completion)
            if (targetProgressRef.current < 90) {
              targetProgressRef.current = 90;
              lastUpdateTimeRef.current = Date.now();
            }
          } else {
            // Update progress for negotiation data (near completion)
            if (targetProgressRef.current < 90) {
              targetProgressRef.current = 90;
              lastUpdateTimeRef.current = Date.now();
            }
          }
        }
      }

      // Set progress if available, using it to update targetProgress
      if (latestUpdate.progress !== undefined) {
        if (latestUpdate.progress > targetProgressRef.current) {
          targetProgressRef.current = latestUpdate.progress;
          lastUpdateTimeRef.current = Date.now();
        }
      }
    }
  }, [safeStreamUpdates, isLightAnalysis]);

  // Use real progress if available to update targetProgress
  useEffect(() => {
    if (
      realProgress !== undefined &&
      realProgress > targetProgressRef.current
    ) {
      targetProgressRef.current = realProgress;
      lastUpdateTimeRef.current = Date.now();
    }
  }, [realProgress]);

  // Use current phase to update the message and handle completion
  useEffect(() => {
    if (currentPhase === "existing_analysis" || currentPhase === "complete") {
      targetProgressRef.current = 100;
      lastUpdateTimeRef.current = Date.now();
      setLocalIsComplete(true);
    } else if (currentPhase) {
      setCurrentMessage(
        phaseMessages[currentPhase] ||
          `Processing ${currentPhase.replace(/_/g, " ")}...`,
      );

      // Update progress based on phases
      if (currentPhase === "property_data" && targetProgressRef.current < 20) {
        targetProgressRef.current = 20;
        lastUpdateTimeRef.current = Date.now();
      } else if (
        currentPhase === "market_data" &&
        targetProgressRef.current < 50
      ) {
        targetProgressRef.current = 50;
        lastUpdateTimeRef.current = Date.now();
      } else if (currentPhase === "scores" && targetProgressRef.current < 80) {
        targetProgressRef.current = 80;
        lastUpdateTimeRef.current = Date.now();
      }
    }
  }, [currentPhase]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onAnimationComplete={() => {
            if (isOpen) setIsVisible(true);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            // Only allow closing during sign in state
            if (requiresSignIn && e.target === e.currentTarget && onClose) {
              setIsVisible(false);
              onClose();
            }
          }}
        >
          <Card className="relative w-full max-w-sm sm:max-w-md p-6 mx-4 bg-white shadow-2xl rounded-xl">
            {requiresSignIn && onClose && (
              <button
                onClick={() => {
                  setIsVisible(false);
                  onClose();
                }}
                type="button"
                className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close modal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center space-x-2">
                {localIsComplete ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : requiresSignIn ? (
                  <Lock className="w-5 h-5 text-primary" />
                ) : (
                  <Building2 className="w-5 h-5 text-primary" />
                )}
                <h2 className="text-lg font-semibold">
                  {localIsComplete
                    ? "Analysis Complete"
                    : requiresSignIn
                      ? "Sign In Required"
                      : isLightAnalysis
                        ? "Quick Analysis in Progress"
                        : "Full Analysis in Progress"}
                </h2>
              </div>

              {/* City Info */}
              {!localIsComplete && !requiresSignIn && (
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="text-sm">
                    {city}, {state}
                  </span>
                </div>
              )}

              {/* Progress Bar */}
              {!localIsComplete && !requiresSignIn && (
                <>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1 px-1">
                    <span>
                      {phaseMessages[currentPhase || ""] ||
                        currentPhase?.replace(/_/g, " ") ||
                        "Initializing analysis..."}
                    </span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-1.5" />
                </>
              )}

              {requiresSignIn ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center gap-5 py-4"
                >
                  <div className="text-center space-y-3">
                    <div className="flex justify-center">
                      <div className="relative">
                        <span className="text-3xl sm:text-4xl">🏠</span>
                        <span className="absolute -right-1 -bottom-1 text-xl sm:text-2xl">
                          ✨
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900">
                        Start Your Free Analysis
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 max-w-md mx-auto">
                        Sign in to analyze {address} and discover potential
                        savings on your purchase.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                        <span>Free Analysis</span>
                      </div>
                      <div className="w-1 h-1 rounded-full bg-gray-300" />
                      <div className="flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5 text-blue-500" />
                        <span>Secure Sign In</span>
                      </div>
                    </div>
                  </div>

                  <div className="w-full space-y-2">
                    <button
                      ref={buttonRef}
                      onClick={onSignInClick}
                      type="button"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 transition-all shadow-sm hover:shadow-md"
                    >
                      <img
                        src="/images/login/google.png"
                        alt="Google"
                        className="w-4 h-4"
                      />
                      <span className="font-semibold text-sm">
                        Continue with Google
                      </span>
                    </button>

                    <p className="text-center text-[10px] text-gray-500">
                      By continuing, you agree to our{" "}
                      <a
                        href="/terms"
                        className="underline hover:text-gray-700"
                      >
                        Terms
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy"
                        className="underline hover:text-gray-700"
                      >
                        Privacy Policy
                      </a>
                    </p>
                  </div>
                </motion.div>
              ) : localIsComplete && (propertySlug || analysisId) ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center gap-5 py-4"
                >
                  <div className="text-center space-y-2">
                    <span className="text-3xl">
                      {isLightAnalysis ? "✨" : "🎉"}
                    </span>
                    <h3 className="text-base font-semibold">
                      {isLightAnalysis
                        ? "Your Quick Analysis Preview is Ready!"
                        : "Your property analysis is ready!"}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {isLightAnalysis
                        ? `Get a sneak peek at ${address}. Sign in for full AI-powered analysis.`
                        : `We've analyzed every aspect of ${address}.`}
                    </p>
                  </div>
                  <div className="w-full space-y-2">
                    <Link
                      href={
                        analysisId
                          ? `/property-analyzer/${analysisId}`
                          : `/properties/${propertySlug}`
                      }
                      className="w-full"
                    >
                      <Button
                        size="lg"
                        className="w-full rounded-full font-semibold bg-canary hover:bg-canary/90 text-black"
                      >
                        {isLightAnalysis ? "View Preview" : "View Analysis"}
                      </Button>
                    </Link>
                    {isLightAnalysis && (
                      <div className="flex items-center gap-1.5 justify-center text-xs text-muted-foreground">
                        <Lock className="w-3 h-3" />
                        <span>Sign in required for full analysis</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : (
                <StreamingInsights
                  streamUpdates={safeStreamUpdates}
                  currentPhase={currentPhase}
                  isLightAnalysis={isLightAnalysis}
                  propertyInfo={{
                    address,
                    city,
                    state,
                    ...propertyInfo,
                  }}
                />
              )}
            </div>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
